<template>
  <div class="playlist-link-viewer" :style="containerStyle">
    <div
      v-if="slide.linkUrl && showPrimaryIframe"
      class="iframe-wrapper primary-iframe"
      :class="{ active: activePrimaryIframe }"
      :style="iframeWrapperStyle"
    >
      <iframe
        ref="primaryIframe"
        class="iframe-container"
        :src="primaryIframeSource"
        scrolling="no"
        :style="iframeStyle"
        @load="onPrimaryIframeLoad"
      />
    </div>

    <div
      v-if="slide.linkUrl && showSecondaryIframe"
      class="iframe-wrapper secondary-iframe"
      :class="{ active: !activePrimaryIframe }"
      :style="iframeWrapperStyle"
    >
      <iframe
        ref="secondaryIframe"
        class="iframe-container"
        :src="secondaryIframeSource"
        scrolling="no"
        :style="iframeStyle"
        @load="onSecondaryIframeLoad"
      />
    </div>

    <div v-if="!slide.linkUrl" class="link-placeholder">
      <i class="material-icons-outlined align-middle">link</i>
      Website Link
    </div>
  </div>
</template>

<script>
  import { duration } from 'moment';
  import { getRBGAColor } from '@/helpers/utils';

  export default {
    name: 'PlaylistLinkViewer',

    props: {
      slide: {
        type: Object,
        required: true,
      },
      show: {
        type: Boolean,
        default: false,
      },
      zoom: {
        type: Number,
        default: 1,
      },
    },

    data() {
      return {
        refreshTimer: null,
        refreshCount: 0,
        activePrimaryIframe: true,
        showPrimaryIframe: true,
        showSecondaryIframe: false,
        loadingTimeout: null,
        transitionInProgress: false,
      };
    },

    computed: {
      containerStyle() {
        return {
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          backgroundColor: getRBGAColor(
            this.slide.backgroundColor || '#ffffff',
            (this.slide.opacity || 100) / 100,
          ),
          position: 'relative',
        };
      },

      iframeZoom() {
        return (this.slide.zoom || 100) / 100;
      },

      iframeWrapperStyle() {
        return {
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          transition: 'opacity 0.3s ease-in-out',
          zIndex: 1,
        };
      },

      iframeStyle() {
        var zoom = this.iframeZoom;

        return {
          position: 'absolute',
          top: 0,
          left: 0,
          width: zoom > 0 ? 100 / zoom + '%' : '100%',
          height: zoom > 0 ? 100 / zoom + '%' : '100%',
          border: 0,
          pointerEvents: 'none',
          overflow: 'hidden',
          transform: 'scale(' + zoom + ')',
          transformOrigin: '0 0',
        };
      },

      primaryIframeSource() {
        if (!this.slide.linkUrl) return '';

        const baseUrl = this.slide.linkUrl;
        const hasQueryParams = baseUrl.includes('?');
        const separator = hasQueryParams ? '&' : '?';

        return `${baseUrl}${separator}_refresh=${this.refreshCount}&_iframe=primary`;
      },

      secondaryIframeSource() {
        if (!this.slide.linkUrl) return '';

        const baseUrl = this.slide.linkUrl;
        const hasQueryParams = baseUrl.includes('?');
        const separator = hasQueryParams ? '&' : '?';

        return `${baseUrl}${separator}_refresh=${this.refreshCount + 1}&_iframe=secondary`;
      },

      refreshSettings() {
        return {
          refresh: this.slide.refresh || false,
          refreshTime: this.slide.refreshTime || 24,
          timeUnit: this.slide.timeUnit || 'hours',
        };
      },
    },

    watch: {
      show(newValue) {
        if (newValue) {
          this.setupRefreshTimer();
        } else {
          this.clearRefreshTimer();
        }
      },

      slide: {
        handler() {
          this.setupRefreshTimer();
        },
        deep: true,
      },
    },

    mounted() {
      if (this.show) {
        this.setupRefreshTimer();
      }
    },

    beforeDestroy() {
      this.clearRefreshTimer();
      this.clearLoadingTimeout();
    },

    methods: {
      setupRefreshTimer() {
        this.clearRefreshTimer();

        if (
          this.refreshSettings.refresh &&
          this.refreshSettings.refreshTime &&
          this.refreshSettings.refreshTime > 0 &&
          this.slide.linkUrl &&
          this.show
        ) {
          const refreshInterval = duration(
            this.refreshSettings.refreshTime,
            this.refreshSettings.timeUnit,
          ).asMilliseconds();

          this.refreshTimer = setInterval(this.refreshLink, refreshInterval);
        }
      },

      clearRefreshTimer() {
        if (this.refreshTimer) {
          clearInterval(this.refreshTimer);
          this.refreshTimer = null;
        }
      },

      clearLoadingTimeout() {
        if (this.loadingTimeout) {
          clearTimeout(this.loadingTimeout);
          this.loadingTimeout = null;
        }
      },

      refreshLink() {
        if (this.transitionInProgress) {
          return;
        }

        this.transitionInProgress = true;
        this.refreshCount++;

        if (this.activePrimaryIframe) {
          this.showSecondaryIframe = true;
        } else {
          this.showPrimaryIframe = true;
        }

        this.loadingTimeout = setTimeout(() => {
          this.switchActiveIframe();
        }, 5000);
      },

      switchActiveIframe() {
        this.activePrimaryIframe = !this.activePrimaryIframe;

        setTimeout(() => {
          if (this.activePrimaryIframe) {
            this.showSecondaryIframe = false;
          } else {
            this.showPrimaryIframe = false;
          }
          this.transitionInProgress = false;
        }, 500);
      },

      onPrimaryIframeLoad() {
        if (!this.activePrimaryIframe && this.transitionInProgress) {
          this.clearLoadingTimeout();
          this.switchActiveIframe();
        }
      },

      onSecondaryIframeLoad() {
        if (this.activePrimaryIframe && this.transitionInProgress) {
          this.clearLoadingTimeout();
          this.switchActiveIframe();
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .playlist-link-viewer {
    position: relative;
    overflow: hidden;
  }

  .iframe-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
    z-index: 1;

    &.active {
      opacity: 1;
      z-index: 2;
    }
  }

  .iframe-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
    pointer-events: none;
    overflow: hidden;
  }

  .link-placeholder {
    overflow: hidden;
    display: flex;
    flex-direction: column;
    text-align: center;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-items: center;
    color: #757575;
    font-size: 32px;

    i {
      font-size: 64px;
    }
  }
</style>
